import React, {useState, useCallback, useEffect, useMemo} from 'react'
import SMCTextInput from '../../components/Simple/SMCTextInput';
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../components/Simple/SMCButton';
import SMCModal from '../../components/core/SMCModal';
import {ObjectTypes} from '../../api/consts';
import Api from '../../api/SMPFetchHelper';
import SMCTable from '../../components/core/SMCTable/SMCTable';
import {UserGetListSchema, UserStatuses, UserFilterSchema} from '../../api/DBSchema/User';
import DescriptionCell from '../../components/core/SMCTable/CommonCells/DescriptionCell';
import connectedLogo from '../../assets/images/gateway/connected.png';
import disconnectedLogo from '../../assets/images/gateway/disconnected.png';
import disabledLogo from '../../assets/images/gateway/disabled.png';
import SMCLabel from '../../components/Simple/SMCLabel';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useSMCErrorHandler} from '../../utils/errorUtils';

const statusCell = React.memo(({value}) => {
    const { t } = useTranslation();
    switch (value) {
        case UserStatuses.connected:
            return <img src={connectedLogo} title={t('SMC_GATEWAY_STATUS_CONNECTED')} alt="Connected" />
        case UserStatuses.disconnected:
            return <img src={disconnectedLogo} title={t('SMC_GATEWAY_STATUS_DISCONNECTED')} alt="Disconnected" />
        case UserStatuses.disabled:
            return <img src={disabledLogo} title={t('SMC_GEN_DISABLED')} alt="Disabled" />
        default:
            return null;
    }
});

function SetUserModal({
    isOpen,
    onRequestClose,
    onSuccess,
    disableSelectById,
    selectMultiple,
    isOwnerModal
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const userTableColumns = useMemo(() => ([
        { id: 'name',        accessor: UserGetListSchema.name,        header: t('SMP.NAME'),                   width: 130                                         },
        { id: 'description', accessor: UserGetListSchema.description, header: '',                              width: 32,  align: 'center', Cell: DescriptionCell },
        { id: 'firstName',   accessor: UserGetListSchema.firstName,   header: t('SMP.USERS.TABLE.FIRST_NAME'), width: 115                                         },
        { id: 'lastName',    accessor: UserGetListSchema.lastName,    header: t('SMP.USERS.TABLE.LAST_NAME'),  width: 115                                         },
        { id: 'company',     accessor: UserGetListSchema.company,     header: t('SMP.USERS.TABLE.COMPANY'),    width: 120                                         },
        { id: 'email',       accessor: UserGetListSchema.email,       header: t('SMP.USERS.TABLE.EMAIL'),      width: 150                                         },
        { id: 'phone',       accessor: UserGetListSchema.phone,       header: t('SMP.USERS.TABLE.PHONE'),      width: 110                                         },
        { id: 'role',        accessor: UserGetListSchema.role,        header: t('SMP.USERS.TABLE.ROLE'),       width: 100                                         },
        { id: 'status',      accessor: UserGetListSchema.status,      header: t('SMP.USERS.TABLE.STATUS'),     width: 55,  align: 'center', Cell: statusCell      }
    ]), [t]);

    const [selectedObjects, setSelectedObjects] = useState([]);
    const [isTableDataLoading, setIsTableDataLoading] = useState(false);
    const [userTableData, setUserTableData] = useState([]);
    const [userSeachText, setUserSeachText] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);

    const handleTableSelect = useCallback((selectedObjects) => {
        let newSelectedObjects = [...selectedObjects];
        if (!selectMultiple) {
            // If selected 2, and only alowed one, replace the originally selected with the new one
            if (newSelectedObjects.length === 2) {
                newSelectedObjects = [newSelectedObjects[1]]
            }
            // Not alowed multiselect
            else if (newSelectedObjects.length > 2) {
                return;
            }
        }
        setSelectedObjects(newSelectedObjects);
    }, [selectMultiple]);

    const loadData = useCallback(() => {
        setIsTableDataLoading(true);
        Api.fetchObjectList(ObjectTypes.USER, 0, null, null, null, {[UserFilterSchema.freeTextSearch]: {value: userSeachText}})
        .then((resp) => {
            setUserTableData(resp.data.data);
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            setErrorMessage(errorMessage);
        })
        .finally(() => {
            setIsTableDataLoading(false);
        });
    }, [userSeachText, getErrorMessage]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSetUser = useCallback(() => {
        if (selectedObjects.length > 0) {
            onSuccess(selectedObjects);
        }
    }, [selectedObjects, onSuccess]);

    return (
        <SMCModal
            title={isOwnerModal ? 'Set owner' : 'Set user'}
            height={620}
            width={690}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            errorMessage={errorMessage}
            reloadData={loadData}
        >
            <SMCModalBody>
                <SMCLabel bold>{t('SMC_USERWIZ_CHOOSE')}</SMCLabel>
                <SMCTextInput
                    suffixIcon="search"
                    allowClearAll
                    placeholder={t('SMP.SEARCH')}
                    value={userSeachText}
                    onChange={e => setUserSeachText(e.target.value)}
                />
                <SMCTable
                    loading={isTableDataLoading}
                    content={userTableData}
                    columns={userTableColumns}
                    width={630}
                    height={350}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={handleTableSelect}
                    rowIdSelector={obj => obj[UserGetListSchema.name]}
                    isRowSelectable={obj => Array.isArray(disableSelectById) ? !disableSelectById.includes(obj[UserGetListSchema.name]) : true}
                    hasCheckAll={selectMultiple}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={handleSetUser} disabled={selectedObjects.length === 0}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {isOwnerModal ? 'Set Owner' : 'Set User'}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default SetUserModal;